import {useCallback, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import episodeIcon from '@assets/episode/episode-icon.png';
import {createCx} from '@utils/cx';
import styles from './EpisodeHeader.module.less';
import {useProjectId} from '@/hooks/useProjectId';
import {useStoryboardList} from '@/hooks/useStoryboard';
import {CanvasType} from '@/constants/canvas';
import {AuditMaterialStatusValue} from '@/services/storyboardService/interface';
import {CanvasFrom, useGoCanvas} from '@/hooks/useGoCanvas';

import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import Icon from '@/components/common/Icon';
import {usePermission} from '@/hooks/usePermission';
import {PERMISSIONS} from '@/types/permission';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';
import ColumnSettings from '@/pages/episode/components/ColumnSettings';
import {useColumnSettings} from '@/pages/episode/hooks/useColumnSettings';

const cx = createCx(styles);

interface EpisodeHeaderProps {
    onTitleClick?: () => void;
}

const EpisodeHeader: React.FC<EpisodeHeaderProps> = ({onTitleClick}) => {
    const navigate = useNavigate();
    const projectId = useProjectId();
    const [searchParams] = useSearchParams();
    const episodeId = Number(searchParams.get('episodeId'));
    const navigateCanvasHandler = useGoCanvas();

    const {data} = useStoryboardList(projectId, episodeId);
    const title = data ? `${data.projectName}｜第 ${data.episodeNo} 集` : '';
    const {sendClickLog, sendShowLog} = useTracking();

    /** 检查分镜视频审核状态：none / partial / all */
    const storyboardVideoStatus = useMemo(() => {
        const storyboards = data?.storyboard;
        if (!storyboards || storyboards.length === 0) return 'none';

        let approvedCount = 0;
        for (const sb of storyboards) {
            const hasApprovedVideo = sb.videoMaterials?.some(
                (v) => v.remarkStatus === AuditMaterialStatusValue.Passed,
            );
            if (hasApprovedVideo) approvedCount++;
        }

        if (approvedCount === 0) return 'none';
        if (approvedCount === storyboards.length) return 'all';
        return 'partial';
    }, [data?.storyboard]);

    const handleGoCanvas = useCallback(() => {
        if (!projectId || !episodeId) return;
        sendClickLog({value: TrackEvents.STORYBOARD_TABLE_GO_CANVAS_BTN});
        navigateCanvasHandler({
            canvasType: CanvasType.EPISODE,
            episodeId,
            from: CanvasFrom.EPISODE,
        });
    }, [projectId, episodeId, navigateCanvasHandler, sendClickLog]);

    const handleGoBack = useCallback(() => {
        sendClickLog({value: TrackEvents.STORYBOARD_TABLE_BACK_BTN});
        if (window.history.state?.idx > 0) {
            void navigate(-1);
            return;
        }

        void navigate(projectId ? `/workspace?projectId=${projectId}` : '/workspace');
    }, [navigate, projectId, sendClickLog]);

    const handleGoEdit = useCallback(() => {
        sendClickLog({value: TrackEvents.STORYBOARD_TABLE_GO_EDIT_BTN});
        if (storyboardVideoStatus === 'all') {
            window.open(
                `/builder/editor?mjId=${projectId}&mjEpId=${episodeId}&source=manju_storyboard_table`,
                '_blank',
            );
            return;
        }

        if (storyboardVideoStatus === 'partial') {
            sendShowLog({value: TrackEvents.STORYBOARD_PANEL_UNFINISHED_DIALOG});
            Modal.confirm({
                title: '分镜未全部产出',
                content:
                    '当前仅部分分镜已生成并完成审核，你可以先进入剪辑器进行编辑。仅首次进入编辑器时，分镜素材自动上轨。未生成的分镜暂不可编辑，生成后将自动补充至素材库。',
                cancelText: '返回分镜',
                okText: '进入编辑器',
                onCancel: () => {
                    sendClickLog({value: TrackEvents.STORYBOARD_PANEL_UNFINISHED_BACK});
                },
                onOk: () => {
                    sendClickLog({value: TrackEvents.STORYBOARD_PANEL_UNFINISHED_ENTER});
                    window.open(
                        `/builder/editor?mjId=${projectId}&mjEpId=${episodeId}&source=manju_storyboard_table`,
                        '_blank',
                    );
                },
            });
        }
    }, [storyboardVideoStatus, projectId, episodeId, sendClickLog, sendShowLog]);

    const editDisabled = storyboardVideoStatus === 'none';
    const canToClip = usePermission(PERMISSIONS.STORYBOARD.TO_CLIP);
    const canGenerate = usePermission(PERMISSIONS.STORYBOARD.GENERATE);

    // 列显隐设置（visibleMap 存于 useEpisodeStore，与 EpisodePage 共享）
    const {settings, toggleColumn, toggleAll} = useColumnSettings(
        episodeId,
        projectId,
        data?.columns?.config ?? [],
    );

    const handleSettingOpenChange = useCallback(
        (open: boolean) => {
            sendClickLog({
                value: TrackEvents.STORYBOARD_TABLE_DISPLAY_SETTING_BTN,
                ext: {
                    act: open ? 'open' : 'close',
                },
            });
        },
        [sendClickLog],
    );

    const handleToggleAll = useCallback(() => {
        toggleAll(true);
        sendClickLog({
            value: TrackEvents.STORYBOARD_TABLE_DISPLAY_ALL_BTN,
            ext: {
                // eslint-disable-next-line camelcase
                after_status: 'all_selected',
            },
        });
    }, [toggleAll, sendClickLog]);

    const handleToggleColumn = useCallback(
        (key: string) => {
            const currentVisible = settings.find((s) => s.key === key)?.visible ?? true;
            toggleColumn(key);
            sendClickLog({
                value: TrackEvents.STORYBOARD_TABLE_DISPLAY_OPTION_TOGGLE,
                ext: {
                    // eslint-disable-next-line camelcase
                    column_name: key,
                    // eslint-disable-next-line camelcase
                    after_selected: !currentVisible,
                },
            });
        },
        [toggleColumn, settings, sendClickLog],
    );

    return (
        <header className={styles.episodeHeader}>
            {/* 左侧：项目图标 + 标题 + 展示设置 */}
            <div className={styles.left}>
                <div className={cx('iconBox', 'clickable')} onClick={handleGoBack}>
                    <img src={episodeIcon} className={styles.episodeIcon} alt="episode" />
                </div>
                <button className={styles.titleBtn} onClick={onTitleClick}>
                    <span className={styles.titleText}>{title}</span>
                </button>
                {!!data?.columns?.config?.length && (
                    <ColumnSettings
                        settings={settings}
                        toggleColumn={handleToggleColumn}
                        toggleAll={handleToggleAll}
                        onOpenChange={handleSettingOpenChange}
                    />
                )}
            </div>

            {/* 右侧：操作按钮 */}
            <nav className={styles.right}>
                {canToClip && (
                    <Button
                        type="text"
                        icon={<Icon name="AijBasicJianji" size={16} />}
                        onClick={handleGoEdit}
                        disabled={editDisabled}
                    >
                        去剪辑
                    </Button>
                )}
                {canGenerate && (
                    <Button
                        type="secondary"
                        icon={<Icon name="AijBasicHuabuchuangzuo" size={14} />}
                        onClick={handleGoCanvas}
                    >
                        去画布创作
                    </Button>
                )}
            </nav>
        </header>
    );
};

export default EpisodeHeader;
